import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Box from '@mui/material/Box';

import type { LinkedComponentProps } from 'components/ContentfulContent/LinkedComponent/LinkedComponent';
import type { SeaStageProps } from 'components/ContentfulContent/Stages/SeaStage/SeaStage';
import type { SeaStageWithMultiBadgesProps } from 'components/ContentfulContent/Stages/SeaStageWithMultiBadges/SeaStageWithMultiBadges';
import {
  SeaStageWithBanner,
  type SeaStageWithBannerProps,
} from 'components/ContentfulContent/Stages/SeaStageWithBanner/SeaStageWithBanner';
import type { TextWithImageProps } from './TextWithImage/TextWithImage';

import { isLive } from 'lib/environment/environmentHelper';

// Components that we always want to have ready to render
// are declared here and not dynamically imported.
import { UnknownComponent } from './UnknownComponent/UnknownComponent';
import { LinkedComponent } from 'components/ContentfulContent/LinkedComponent/LinkedComponent';
import { SeaStage } from 'components/ContentfulContent/Stages/SeaStage/SeaStage';
import { SeaStageWithMultiBadges } from 'components/ContentfulContent/Stages/SeaStageWithMultiBadges/SeaStageWithMultiBadges';

const componentMap = {
  // Dynamic Content can be imported like this - currently we know which content is coming.
  // As soon as we have more variety of content, we can switch to dynamic imports like this:
  // linkedComponent: dynamic<LinkedComponentProps>(() =>
  //   import('components/ContentfulContent/LinkedComponent/LinkedComponent').then(
  //     (module) => module.LinkedComponent,
  //   ),
  // ),
  linkedComponent: (props: LinkedComponentProps) => LinkedComponent(props), // Switch to dynamic import later
  seaStage: (props: SeaStageProps) => SeaStage(props), // Switch to dynamic import later
  seaStageWithMultibadge: (props: SeaStageWithMultiBadgesProps) =>
    SeaStageWithMultiBadges(props),
  seaStageWithBanner: (props: SeaStageWithBannerProps) =>
    SeaStageWithBanner(props),
  textWithImage: dynamic<TextWithImageProps>(() =>
    import('components/ContentfulContent/TextWithImage/TextWithImage').then(
      (module) => module.TextWithImage,
    ),
  ),
  // someComponent: () => null, // Deprecated components can be omitted like this
};

export const ContentfulItem = (item: any) => {
  const router = useRouter();

  const displayBoundingBox =
    router.isReady &&
    typeof router.query?.displayBox !== 'undefined' &&
    !isLive();

  const componentName: string = item?.sys?.contentType?.sys.id;
  const isInline = item?.isInline || false;

  if (!componentName) return null;

  // @ts-ignore
  const Component = componentMap[componentName];
  if (!Component && isLive()) return null;
  if (!Component && !isLive()) return <UnknownComponent name={componentName} />;

  if (displayBoundingBox && !isInline) {
    return (
      <Box
        sx={{
          border: '1px solid magenta',
        }}
      >
        <Component {...item.fields} isInline={isInline} />
      </Box>
    );
  }

  // @ts-ignore
  return <Component {...item.fields} isInline={isInline} />;
};
